import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  TemplateRef,
  viewChild
} from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { ButtonComponent } from '../../../../common/components/button/button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { DisplayMessage } from '../../sb-model/display-message';
import { MatIconModule } from '@angular/material/icon';
import { from, switchMap } from 'rxjs';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatIconButton } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { DisplayMessageFile } from '../../sb-model/display-message-file';
import { MatrixService } from '../../../../services/matrix/matrix.service';
import { MediaService } from '../../../../services/media/media.service';
import { DisplayMessageFileAdapter } from '../../sb-model/display-message-file-adapter';
import { ChatMessageService } from '../../../../services/chat-message/chat-message.service';
import { DisplayItem } from '../../sb-model/display-item';
import { downloadFile, FileService } from '../../../../services/file/file.service';
import { MediaSettingsTestIds } from './sb-media-settings.test-ids';
import { HeaderActionsService } from '../../../../services/dialog/header-actions-service';

@Component({
  selector: 'app-sb-media-settings',
  imports: [MatListModule, ButtonComponent, DatePipe, MatIconModule, MatCheckbox, MatIconButton, MatPaginatorModule],
  templateUrl: './sb-media-settings.component.html',
  styleUrls: ['./sb-media-settings.component.scss']
})
/**
 * die hochgeladenen Dateien im tab Medien in den Raumeinstellungen anzeigt. Begründung: Dies ist eine Child Component Es wird keine
 * AFO dadurch erfüllt.
 */
export class SbMediaSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly paginator = viewChild.required(MatPaginator);
  readonly mediaHeaderActionsTpl = viewChild.required('mediaHeaderActionsTpl', { read: TemplateRef });
  mediaMessages: DisplayMessageFile[] = [];
  selectedMessages = new Set<string>();
  anySelected = false;
  viewMode: 'list' | 'grid' = 'list';
  roomId = input<string>();
  pageSize = this.viewMode === 'grid' ? 20 : 9;
  currentPage = 0;
  displayedMediaMessages: DisplayMessageFile[] = [];
  readonly headerActionsService = inject(HeaderActionsService);
  protected readonly testIds = MediaSettingsTestIds;
  protected mediaService = inject(MediaService);
  protected fileService = inject(FileService);
  private readonly clientService = inject(MatrixService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarService);
  private readonly chatMessageService = inject(ChatMessageService);

  ngOnInit(): void {
    // TODO: Falls in die Vergangenheit zurückgegangen werden soll
    //  via this.clientService.client.scrollback(room) - wie weit?
    //  Dazu müsste man scrollback aufrufen und den ChatRoom im ChatStore
    //  mit der erweiterten Timeline neu initialisieren.
    //  (Scrollback-Funktionalität noch nicht implementiert per 14.08.2025.)
    this.chatMessageService.messages$
      .pipe(
        switchMap(messages => from(this.filterDecryptableMessages(messages))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(decryptableMessages => {
        this.mediaMessages = [...decryptableMessages].sort(
          (a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0)
        );
        this.updateDisplayedMessages();
      });
  }

  ngAfterViewInit(): void {
    this.headerActionsService.tpl$.next(this.mediaHeaderActionsTpl());
  }

  ngOnDestroy(): void {
    this.headerActionsService.tpl$.next(null);
  }

  handlePageEvent(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateDisplayedMessages();
  }

  getFileIcon(message: DisplayMessageFile): string {
    const mimeType = message.mimetype || '';

    if (mimeType.includes('image')) return 'image';
    if (mimeType.includes('video')) return 'play';
    if (mimeType.includes('audio')) return 'play';
    if (mimeType.includes('pdf')) return 'file-pdf';
    if (mimeType.includes('word')) return 'word';
    return 'file';
  }

  getFileSize(message: DisplayMessageFile): number {
    return message.size || 0;
  }

  toggleMessageSelection(event: Event | MatCheckboxChange, messageId: string | undefined): void {
    if (!messageId) return;

    const isChecked = event instanceof MatCheckboxChange ? event.checked : !this.selectedMessages.has(messageId);

    if (isChecked) {
      this.selectedMessages.add(messageId);
    } else {
      this.selectedMessages.delete(messageId);
    }

    this.anySelected = this.selectedMessages.size > 0;
  }

  async downloadSelectedFiles(): Promise<void> {
    if (!this.anySelected) {
      this.snackbarService.error('Keine Dateien zum Herunterladen ausgewählt');
      return;
    }

    const selectedFiles = this.mediaMessages.filter(msg => msg.eventId && this.selectedMessages.has(msg.eventId));

    if (selectedFiles.length === 1) {
      const confirmed = await this.mediaService.handleDownloadDialog();
      if (!confirmed) return;
      await this.performDownload(selectedFiles[0]);
    } else {
      const confirmed = await this.mediaService.handleBulkDownloadDialog(selectedFiles.length);
      if (!confirmed) return;

      for (const message of selectedFiles) {
        await this.performDownload(message);
      }
    }
  }

  getDownloadButtonText(): string {
    if (!this.anySelected) return 'Herunterladen';
    return `${this.selectedMessages.size}  Herunterladen`;
  }

  getFileTypeDisplayName(message: DisplayMessageFile): string {
    return message.name || message.body || 'Datei';
  }

  setViewMode(mode: 'list' | 'grid'): void {
    this.viewMode = mode;
    this.pageSize = mode === 'grid' ? 20 : 9;
    this.currentPage = 0;
    const paginator = this.paginator();
    if (paginator) {
      paginator.pageIndex = 0;
    }
    this.updateDisplayedMessages();
  }

  protected isFileAllowed(message: DisplayMessageFile): boolean {
    return this.fileService.isAllowedMimeType(message.mimetype ?? '');
  }

  private updateDisplayedMessages(): void {
    const startIndex = this.currentPage * this.pageSize;
    this.displayedMediaMessages = this.mediaMessages.slice(startIndex, startIndex + this.pageSize);
  }

  private async performDownload(message: DisplayMessageFile): Promise<void> {
    const content = message.content;
    const mxcUrl = content['file']?.url || content['url'];

    if (!mxcUrl?.startsWith('mxc://')) {
      this.snackbarService.error('Invalid file URL');
      return;
    }

    if (!this.fileService.isAllowedMimeType(message.mimetype ?? '')) {
      this.snackbarService.error('Dateityp nicht erlaubt');
      return;
    }

    const downloadBlob = content['file']
      ? await this.fileService.decryptFile(content).catch(error => {
          console.error('Download failed:', error);
          message.isValid = false;
          this.snackbarService.error('Download failed');
        })
      : await this.fileService.downloadSource(mxcUrl).catch(error => {
          console.error('Download failed:', error);
          message.isValid = false;
          this.snackbarService.error('Download failed');
        });

    if (!downloadBlob) {
      throw new Error('There is no download available');
    }
    const isValid = await this.mediaService.validateDecryptedBlob(downloadBlob, message.mimetype);
    if (!isValid) {
      this.snackbarService.error(`${message.name} ist keine sichere Datei und kann daher nicht runtergeladen werden`);
      return;
    }
    message.blob = downloadBlob;
    message.url = this.clientService.client.mxcUrlToHttp(mxcUrl) ?? '';
    message.isValid = true;
    message.isLoaded = true;

    downloadFile(downloadBlob, message.name);
  }

  private async filterDecryptableMessages(messages: DisplayItem[]): Promise<DisplayMessageFile[]> {
    const mediaMessages = [] as DisplayMessageFile[];
    for (const message of messages) {
      if (!(message instanceof DisplayMessage)) {
        continue;
      }

      if (!this.isMediaMessage(message)) {
        continue;
      }

      const { content } = message;
      if (content['url']?.startsWith('mxc://') && !content['file']) {
        mediaMessages.push(this.transformToMediaMessage(message));
      } else {
        const decryptedBlob = await this.fileService.decryptFile(content);
        if (decryptedBlob.size > 0) {
          mediaMessages.push(this.transformToMediaMessage(message));
        }
      }
    }

    return mediaMessages;
  }

  private isMediaMessage(message: DisplayMessage | DisplayMessageFile): boolean {
    if (!message.content?.['body']) {
      return false;
    }

    const content = message.content;
    const msgtype = content['msgtype'] as string;
    return (
      ['m.file', 'm.image', 'm.video', 'm.audio'].includes(msgtype) ||
      content['file'] !== undefined ||
      content['url']?.startsWith('mxc://')
    );
  }

  private transformToMediaMessage(message: DisplayMessage): DisplayMessageFile {
    return DisplayMessageFileAdapter.messageToMessageFile(message);
  }
}
